// src/store/slices/notificationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import notificationApi from "api/notificationApi";

export const fetchNotifications = createAsyncThunk(
  "notification/fetchAll",
  async ({ page = 1, pageSize = 10 }, { rejectWithValue }) => {
    try {
      const res = await notificationApi.getAll(page, pageSize);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errors?.[0] || "Failed to load");
    }
  },
);

export const fetchUnreadCount = createAsyncThunk(
  "notification/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const count = await notificationApi.getUnreadCount();
      return count;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errors?.[0] || "Failed");
    }
  },
);

// export const fetchNotificationChoices = createAsyncThunk(
//   "notification/fetchChoices",
//   async (_, { rejectWithValue }) => {
//     try {
//       const res = await notificationApi.getNotificationChoices();
//       return res.data.type; // Returns array of { value, label }
//     } catch (e) {
//       return rejectWithValue(
//         e.response?.data?.detail || "Failed to load choices",
//       );
//     }
//   },
// );

export const markNotificationRead = createAsyncThunk(
  "notification/markRead",
  async (id, { rejectWithValue }) => {
    try {
      await notificationApi.markRead(id);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data || "Failed");
    }
  },
);

export const markAllNotificationsRead = createAsyncThunk(
  "notification/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const count = await notificationApi.markAllRead();
      return count;
    } catch (e) {
      return rejectWithValue(e.response?.data || "Failed");
    }
  },
);

export const clearAllNotifications = createAsyncThunk(
  "notification/clearAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await notificationApi.clearAll();
      return res;
    } catch (e) {
      return rejectWithValue(
        e.response?.data || "Failed to clear notifications",
      );
    }
  },
);

const notificationSlice = createSlice({
  name: "notification",
  initialState: {
    list: [],
    unreadCount: 0,
    pagination: { count: 0, page_size: 10 },
    // notificationChoices: [],
    isLoading: false,
    error: null,
  },

  /* ------------------------------------------------------------------ */
  /*  REDUCERS – called from the WebSocket hook                         */
  /* ------------------------------------------------------------------ */
  reducers: {
    /** Called for **every** WS message (initial_state items + live pushes) */
    addRealtimeNotification: (state, action) => {
      const payload = action.payload; // single notification object
      const exists = state?.list?.some((n) => n?.id === payload?.id);
      if (!exists) {
        state?.list?.unshift(payload); // newest first
        if (!payload?.read) state.unreadCount += 1;
      }
    },

    /** Optional – bulk initial state from WS (you can keep both) */
    setInitialState: (state, action) => {
      const { notifications, unread_count } = action.payload;
      // replace list (avoid duplicates)
      state.list = notifications.filter(
        (n) => !state.list.some((existing) => existing.id === n.id),
      );
      // prepend new ones
      state.list.unshift(...notifications);
      state.unreadCount = unread_count;
    },

    clearError: (state) => {
      state.error = null;
    },
  },

  /* ------------------------------------------------------------------ */
  /*  EXTRA REDUCERS – HTTP thunks                                      */
  /* ------------------------------------------------------------------ */
  extraReducers: (b) => {
    // FETCH ALL
    b.addCase(fetchNotifications.pending, (s) => {
      s.isLoading = true;
    })
      .addCase(fetchNotifications.fulfilled, (s, a) => {
        s.isLoading = false;
        s.list = a.payload.data;
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchNotifications.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // FETCH CHOICES
    // b.addCase(fetchNotificationChoices.fulfilled, (s, a) => {
    //   s.notificationChoices = a.payload;
    // });

    // UNREAD COUNT
    b.addCase(fetchUnreadCount.fulfilled, (s, a) => {
      s.unreadCount = a.payload;
    });

    // MARK SINGLE READ
    b.addCase(markNotificationRead.fulfilled, (s, a) => {
      const idx = s.list.findIndex((n) => n.id === a.payload);
      if (idx !== -1) s.list[idx].read = true;
      if (s.unreadCount > 0) s.unreadCount -= 1;
    });

    // MARK ALL READ
    b.addCase(markAllNotificationsRead.fulfilled, (s) => {
      s.list.forEach((n) => (n.read = true));
      s.unreadCount = 0;
    });

    b.addCase(clearAllNotifications.fulfilled, (state) => {
      state.list = [];
      state.unreadCount = 0;
    });
  },
});

export const { addRealtimeNotification, setInitialState, clearError } =
  notificationSlice.actions;

export default notificationSlice.reducer;
